import { AnchorHTMLAttributes, ReactNode } from 'react'
import { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { Icon } from '../icon'
import { mtc, TextColors } from '../../../utils/style-helpers'
import { styles } from './styles'
import { useAnalyticsTrack, Attributes } from '@/features/amplitude'
import { useNavigate } from 'react-router-dom'

export interface ILinkProps extends AnchorHTMLAttributes<HTMLAnchorElement> {
  children?: ReactNode
  className?: string
  color?: TextColors
  disabled?: boolean
  href: string
  iconLeft?: IconDefinition
  iconRight?: IconDefinition
  trackMessage?: string
  trackAttributes?: Attributes
  variant?: 'primary' | 'secondary' | 'noUnderline'
  newPage?: boolean
}

/** True when the browser should handle the click itself: a modifier key (new tab or window) or a non-primary button. */
export const isModifiedClick = (e: React.MouseEvent): boolean =>
  e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey

export const Link = ({
  children,
  className,
  color,
  disabled,
  href,
  iconLeft,
  iconRight,
  variant,
  trackMessage,
  trackAttributes,
  newPage = false,
  onClick,
  ...rest
}: ILinkProps) => {
  const track = useAnalyticsTrack()
  const navigate = useNavigate()

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Track analytics if needed
    if (trackMessage) {
      track(trackMessage, trackAttributes)
    }

    // Call custom onClick if provided
    if (onClick) {
      onClick(e)
      if (e.defaultPrevented) return
    }

    // New-tab clicks and external links are left to the browser
    if (
      newPage ||
      isModifiedClick(e) ||
      href.startsWith('http://') ||
      href.startsWith('https://')
    ) {
      // Don't prevent default - let the browser open the link in a new tab
      return
    }

    // For internal navigation, use React Router
    e.preventDefault()
    navigate(href)
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLAnchorElement>) => {
    if (e.key === 'Enter') {
      // For external links, let the browser handle Enter key naturally
      if (
        newPage ||
        href.startsWith('http://') ||
        href.startsWith('https://')
      ) {
        return
      }
      // For internal links, prevent default and trigger click
      e.preventDefault()
      e.currentTarget.click()
    } else if (e.key === ' ') {
      e.preventDefault()
    }
  }

  return (
    <a
      className={mtc(styles({ disabled, color, variant }), className)}
      href={href}
      target={newPage ? '_blank' : '_self'}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      rel={newPage ? 'noopener noreferrer' : undefined}
      {...rest}
    >
      {iconLeft && (
        <Icon
          icon={iconLeft}
          className="pr-1 right-3 text-gray-500"
          color="currentColor"
        />
      )}

      {children}

      {iconRight && (
        <Icon
          icon={iconRight}
          className="pl-1 right-3 text-gray-500"
          color="currentColor"
        />
      )}
    </a>
  )
}

Link.displayName = 'Link'
